import { Updater } from 'widgets-for-react'
import { subscriptionUpdaters } from '../../../subscription/subscriptionState'
import { initialStopSubscriptionState, StopSubscriptionState } from '../../../subscription/stopSubscription/stopSubscriptionState'
import {
  CurrentPage,
  PageParams,
  RouteBuilders,
  RouteUpdaters,
} from '../../../utils/routes'
import {
  BlogOverviewState,
  initialBlogOverviewState,
} from '../blogs/blogOverview/blogOverviewState'
import {
  CollectionDetailState,
  initialCollectionDetailState,
} from '../collections/collectionDetail/collectionDetailState'
import {
  ComponentDetailState,
  initialComponentDetailState,
} from '../components/componentDetail/componentDetailState'
import {
  ComponentEditState,
  initialComponentEditState,
} from '../components/componentEdit/componentEditState'
import {
  ComponentOverviewState,
  initialComponentOverviewState,
} from '../components/componentOverview/componentOverviewState'
import { ContactState, initialContactState } from '../contact/contactState'
import {
  DashboardState,
  initialDashboardState,
} from '../dashboard/dashboardState'
import {
  DishOverviewState,
  initialDishOverviewState,
} from '../dishes/dishOverview/dishOverviewState'
import { HomeState, initialHomeState } from '../home/home.state'
import {
  KnowledgeTypeOverviewState,
  initialKnowledgeTypeOverviewState,
} from '../knowledge/knowledgeTypeOverview/knowledgeTypeOverviewState'
import {
  KnowledgeTypeTeasersState,
  initialKnowledgeTypeTeasersState,
} from '../knowledge/knowledgeTypeTeasers/knowledgeTypeTeasersState'
import { RootState } from '../rootState'
import { SearchState, initialSearchState } from '../search/searchState'
import {
  SeasonalCalendarState,
  initialSeasonalCalendarState,
} from '../seasonalCalendar/seasonalCalendarState'
import {
  SharepointPageState,
  initialSharepointPageState,
} from '../sharepointPage/sharepointPageState'
import {
  StudentSubscriptionState,
  initialStudentSubscriptionState,
} from '../studentSubscription/studentSubscriptionState'
import {
  parseArrayParam,
  parseCategoryParam,
  parsePageParam,
} from './routesUtils'

type SignupSuccesParameter = { 'signup-success'?: string }
type OriginPathParameter = { originPath?: string }
type PageParameter = { page?: string }
type TopFilterParameter = {
  technique?: string | string[]
  appearance?: string | string[]
  texture?: string | string[]
  allergen?: string | string[]
  type?: string | string[]
}
type LeftFilterParameter = {
  category?: string
  subcategory?: string
  ingredient?: string
}
type SearchParameter = {
  term?: string
  tab?: string
}

export type Pages = {
  notFound: { url: [] }
  homeRedirect: { url: [] }
  home: {
    url: [SignupSuccesParameter]
    pageState: HomeState
  }
  componentOverview: {
    url: [
      PageParameter,
      SignupSuccesParameter,
      TopFilterParameter,
      LeftFilterParameter
    ]
    pageState: ComponentOverviewState
  }
  componentDetail: {
    url: [{ componentIdAndName: string }, SignupSuccesParameter]
    pageState: ComponentDetailState
  }
  componentEdit: {
    url: [{ componentId: string }, SignupSuccesParameter]
    pageState: ComponentEditState
  }
  blogOverview: {
    url: [PageParameter, SignupSuccesParameter]
    pageState: BlogOverviewState
  }
  blogDetail: {
    url: [{ blogSlug: string }, SignupSuccesParameter]
    pageState: SharepointPageState
  }
  search: {
    url: [
      SearchParameter,
      TopFilterParameter,
      PageParameter,
      SignupSuccesParameter
    ]
    pageState: SearchState
  }
  dashboard: { url: [PageParameter]; pageState: DashboardState }
  collectionDetail: {
    url: [{ collectionId: string }, PageParameter]
    pageState: CollectionDetailState
  }
  knowledgeOverview: {
    url: [SignupSuccesParameter]
    pageState: KnowledgeTypeTeasersState
  }
  knowledgeTypeOverview: {
    url: [{ knowledgeTypeTitle: string }, SignupSuccesParameter]
    pageState: KnowledgeTypeOverviewState
  }
  knowledgeDetail: {
    url: [
      { knowledgeTypeTitle: string },
      { knowledgeSlug: string },
      SignupSuccesParameter
    ]
    pageState: SharepointPageState
  }
  seasonalCalendar: {
    url: [SignupSuccesParameter]
    pageState: SeasonalCalendarState
  }
  dishesOverview: {
    url: [PageParameter, SignupSuccesParameter]
    pageState: DishOverviewState
  }
  dishesDetail: {
    url: [{ dishSlug: string }, SignupSuccesParameter]
    pageState: SharepointPageState
  }
  page: { url: [{ pageSlug: string }]; pageState: SharepointPageState }

  contact: { url: [SignupSuccesParameter]; pageState: ContactState }

  accountDetails: { url: [SignupSuccesParameter] }
  updatePayment: { url: [] }
  updatePaymentRedirect: { url: [] }
  thankYouUpdatePayment: { url: [] }
  upgrade: { url: [] }

  subscriptionSuccessRedirect: { url: [] }
  subscriptionSuccess: { url: [] }
  subscriptionFailureRedirect: { url: [] }
  subscriptionFailure: { url: [] }
  subscriptionCancelRedirect: { url: [] }
  subscriptionCancel: { url: [] }
  subscriptionStopPayment: { url: []; pageState: StopSubscriptionState }

  changePaymentMethodSuccessRedirect: { url: [] }
  changePaymentMethodSuccess: { url: [] }
  changePaymentMethodFailureRedirect: { url: [] }
  changePaymentMethodFailure: { url: [] }
  changePaymentMethodCancelRedirect: { url: [] }
  changePaymentMethodCancel: { url: [] }

  payOpenInvoiceSuccessRedirect: { url: [] }
  payOpenInvoiceSuccess: { url: [] }
  payOpenInvoiceFailureRedirect: { url: [] }
  payOpenInvoiceFailure: { url: [] }
  payOpenInvoiceCancelRedirect: { url: [] }
  payOpenInvoiceCancel: { url: [] }

  register: { url: [OriginPathParameter] }
  registerStudent: { url: [OriginPathParameter] }
  registerPromo: { url: [OriginPathParameter] }
  activateAccount: {
    url: [
      OriginPathParameter,
      { userEmail?: string },
      { activationToken?: string },
      { reset?: string }
    ]
  }
  login: { url: [OriginPathParameter, { 'student-flow'?: string }] }
  logout: { url: [] }
  resetPassword: { url: [] }
  requestResetPassword: { url: [] }

  studentSubscription: { url: []; pageState: StudentSubscriptionState }
}

export const initialPage: CurrentPage<Pages> = {
  kind: 'homeRedirect',
  params: {},
}

export const routeBuilders: RouteBuilders<RootState, Pages> = {
  notFound: {
    make: (paramsAndState) => ({
      kind: 'notFound',
      params: paramsAndState.params,
    }),
  },
  homeRedirect: {
    make: (paramsAndState) => ({
      kind: 'homeRedirect',
      params: paramsAndState.params,
    }),
  },
  home: {
    make: (paramsAndState) => ({
      kind: 'home',
      params: paramsAndState.params,
      pageState: initialHomeState,
    }),
  },
  componentOverview: {
    make: (paramsAndState) => ({
      kind: 'componentOverview',
      params: paramsAndState.params,
      pageState: initialComponentOverviewState(
        parsePageParam(paramsAndState.params.page),
        {
          category: paramsAndState.params.category,
          subcategory: paramsAndState.params.subcategory,
          ingredient: paramsAndState.params.ingredient,
        },
        {
          allergens: parseArrayParam(paramsAndState.params.allergen),
          techniques: parseArrayParam(paramsAndState.params.technique),
          appearances: parseArrayParam(paramsAndState.params.appearance),
          textures: parseArrayParam(paramsAndState.params.texture),
          types: parseArrayParam(paramsAndState.params.type),
        }
      ),
    }),
  },
  componentDetail: {
    make: (paramsAndState) => {
      const { params, state } = paramsAndState
      const splitted = params.componentIdAndName.split('-')
      const componentId = splitted.length > 0 ? parseInt(splitted[0]) : NaN
      if (
        state.page.kind === 'componentEdit' &&
        state.page.pageState.kind === 'loaded' &&
        state.page.pageState.value.editingState.kind === 'loaded' &&
        (typeof state.page.pageState.value.editingState.value === 'number'
          ? state.page.pageState.value.editingState.value
          : parseInt(state.page.params.componentId)) === componentId
      ) {
        return {
          kind: 'componentDetail',
          params: params,
          pageState: initialComponentDetailState(componentId, true),
        }
      }
      return {
        kind: 'componentDetail',
        params: params,
        pageState: initialComponentDetailState(componentId, false),
      }
    },
  },
  componentEdit: {
    make: (paramsAndState) => {
      const { params, state } = paramsAndState
      const componentId = parseInt(params.componentId)
      if (state.page.kind === 'componentDetail') {
        const previousSplitted = state.page.params.componentIdAndName.split('-')
        const previousComponentId =
          previousSplitted.length > 0 ? parseInt(previousSplitted[0]) : NaN
        return {
          kind: 'componentEdit',
          params: params,
          pageState: initialComponentEditState(
            componentId,
            state.page.kind === 'componentDetail' &&
              previousComponentId === componentId &&
              state.page.pageState.componentStateState.kind === 'loaded'
              ? state.page.pageState.componentStateState.value.componentData
              : undefined
          ),
        }
      } else
        return {
          kind: 'componentEdit',
          params: params,
          pageState: initialComponentEditState(componentId),
        }
    },
  },
  blogOverview: {
    make: (paramsAndState) => ({
      kind: 'blogOverview',
      params: paramsAndState.params,
      pageState: initialBlogOverviewState(
        parsePageParam(paramsAndState.params.page)
      ),
    }),
  },
  blogDetail: {
    make: (paramsAndState) => ({
      kind: 'blogDetail',
      params: paramsAndState.params,
      pageState: initialSharepointPageState(paramsAndState.params.blogSlug),
    }),
  },
  search: {
    make: (paramsAndState) => {
      const previousPage = paramsAndState.state.page
      return {
        kind: 'search',
        params: paramsAndState.params,
        pageState: initialSearchState(
          parseCategoryParam(paramsAndState.params.tab),
          parsePageParam(paramsAndState.params.page),
          paramsAndState.params.term,
          {
            allergens: parseArrayParam(paramsAndState.params.allergen),
            techniques: parseArrayParam(paramsAndState.params.technique),
            appearances: parseArrayParam(paramsAndState.params.appearance),
            textures: parseArrayParam(paramsAndState.params.texture),
            types: parseArrayParam(paramsAndState.params.type),
          },
          previousPage.kind === 'search' &&
            previousPage.params.term === paramsAndState.params.term &&
            previousPage.params.allergen === paramsAndState.params.allergen &&
            previousPage.params.technique === paramsAndState.params.technique &&
            previousPage.params.appearance ===
              paramsAndState.params.appearance &&
            previousPage.params.texture === paramsAndState.params.texture &&
            previousPage.params.type === paramsAndState.params.type
            ? {
                components:
                  previousPage.pageState.searchTabs.components.totalCount,
                dishes: previousPage.pageState.searchTabs.dishes.totalCount,
                knowledge:
                  previousPage.pageState.searchTabs.knowledge.totalCount,
                collections:
                  previousPage.pageState.searchTabs.collections.totalCount,
                blogs: previousPage.pageState.searchTabs.blogs.totalCount,
              }
            : undefined
        ),
      }
    },
  },
  dashboard: {
    make: (paramsAndState) => ({
      kind: 'dashboard',
      params: paramsAndState.params,
      pageState: initialDashboardState,
    }),
  },
  collectionDetail: {
    make: (paramsAndState) => ({
      kind: 'collectionDetail',
      params: paramsAndState.params,
      pageState: initialCollectionDetailState,
    }),
  },
  knowledgeOverview: {
    make: (paramsAndState) => ({
      kind: 'knowledgeOverview',
      params: paramsAndState.params,
      pageState: initialKnowledgeTypeTeasersState,
    }),
  },
  knowledgeTypeOverview: {
    make: (paramsAndState) => ({
      kind: 'knowledgeTypeOverview',
      params: paramsAndState.params,
      pageState: initialKnowledgeTypeOverviewState(
        paramsAndState.params.knowledgeTypeTitle
      ),
    }),
  },
  knowledgeDetail: {
    make: (paramsAndState) => ({
      kind: 'knowledgeDetail',
      params: paramsAndState.params,
      pageState: initialSharepointPageState(
        paramsAndState.params.knowledgeSlug
      ),
    }),
  },
  seasonalCalendar: {
    make: (paramsAndState) => ({
      kind: 'seasonalCalendar',
      params: paramsAndState.params,
      pageState: initialSeasonalCalendarState,
    }),
  },
  page: {
    make: (paramsAndState) => ({
      kind: 'page',
      params: paramsAndState.params,
      pageState: initialSharepointPageState(paramsAndState.params.pageSlug),
    }),
  },
  contact: {
    make: (paramsAndState) => ({
      kind: 'contact',
      params: paramsAndState.params,
      pageState: initialContactState(
        paramsAndState.state.subscriptionState.userSubscriptions
      ),
    }),
  },
  dishesOverview: {
    make: (paramsAndState) => ({
      kind: 'dishesOverview',
      params: paramsAndState.params,
      pageState: initialDishOverviewState(
        parsePageParam(paramsAndState.params.page)
      ),
    }),
  },
  dishesDetail: {
    make: (paramsAndState) => ({
      kind: 'dishesDetail',
      params: paramsAndState.params,
      pageState: initialSharepointPageState(paramsAndState.params.dishSlug),
    }),
  },

  accountDetails: {
    make: (paramsAndState) => ({
      kind: 'accountDetails',
      params: paramsAndState.params,
    }),
  },
  updatePayment: {
    make: (paramsAndState) => ({
      kind: 'updatePayment',
      params: paramsAndState.params
    }),
  },
  updatePaymentRedirect: {
    make: (paramsAndState) => ({
      kind: 'updatePaymentRedirect',
      params: paramsAndState.params
    }),
  },
  thankYouUpdatePayment: {
    make: (paramsAndState) => ({
      kind: 'thankYouUpdatePayment',
      params: paramsAndState.params
    }),
  },
  upgrade: {
    make: (paramsAndState) => ({
      kind: 'upgrade',
      params: paramsAndState.params,
    }),
  },

  subscriptionSuccessRedirect: {
    make: (paramsAndState) => ({
      kind: 'subscriptionSuccessRedirect',
      params: paramsAndState.params,
    }),
  },
  subscriptionSuccess: {
    make: (paramsAndState) => ({
      kind: 'subscriptionSuccess',
      params: paramsAndState.params,
    }),
  },
  subscriptionFailureRedirect: {
    make: (paramsAndState) => ({
      kind: 'subscriptionFailureRedirect',
      params: paramsAndState.params,
    }),
  },
  subscriptionFailure: {
    make: (paramsAndState) => ({
      kind: 'subscriptionFailure',
      params: paramsAndState.params,
    }),
  },
  subscriptionCancelRedirect: {
    make: (paramsAndState) => ({
      kind: 'subscriptionCancelRedirect',
      params: paramsAndState.params,
    }),
  },
  subscriptionCancel: {
    make: (paramsAndState) => ({
      kind: 'subscriptionCancel',
      params: paramsAndState.params,
    }),
  },
  subscriptionStopPayment: {
    make: (paramsAndState) => ({
      kind: 'subscriptionStopPayment',
      params: paramsAndState.params,
      pageState: initialStopSubscriptionState(paramsAndState.state.subscriptionState.userSubscriptions)
    }),
  },
  changePaymentMethodSuccessRedirect: {
    make: (paramsAndState) => ({
      kind: 'changePaymentMethodSuccessRedirect',
      params: paramsAndState.params,
    }),
  },
  changePaymentMethodSuccess: {
    make: (paramsAndState) => ({
      kind: 'changePaymentMethodSuccess',
      params: paramsAndState.params,
    }),
  },
  changePaymentMethodFailureRedirect: {
    make: (paramsAndState) => ({
      kind: 'changePaymentMethodFailureRedirect',
      params: paramsAndState.params,
    }),
  },
  changePaymentMethodFailure: {
    make: (paramsAndState) => ({
      kind: 'changePaymentMethodFailure',
      params: paramsAndState.params,
    }),
  },
  changePaymentMethodCancelRedirect: {
    make: (paramsAndState) => ({
      kind: 'changePaymentMethodCancelRedirect',
      params: paramsAndState.params,
    }),
  },
  changePaymentMethodCancel: {
    make: (paramsAndState) => ({
      kind: 'changePaymentMethodCancel',
      params: paramsAndState.params,
    }),
  },
  payOpenInvoiceSuccessRedirect: {
    make: (paramsAndState) => ({
      kind: 'payOpenInvoiceSuccessRedirect',
      params: paramsAndState.params,
    }),
  },
  payOpenInvoiceSuccess: {
    make: (paramsAndState) => ({
      kind: 'payOpenInvoiceSuccess',
      params: paramsAndState.params,
    }),
  },
  payOpenInvoiceFailureRedirect: {
    make: (paramsAndState) => ({
      kind: 'payOpenInvoiceFailureRedirect',
      params: paramsAndState.params,
    }),
  },
  payOpenInvoiceFailure: {
    make: (paramsAndState) => ({
      kind: 'payOpenInvoiceFailure',
      params: paramsAndState.params,
    }),
  },
  payOpenInvoiceCancelRedirect: {
    make: (paramsAndState) => ({
      kind: 'payOpenInvoiceCancelRedirect',
      params: paramsAndState.params,
    }),
  },
  payOpenInvoiceCancel: {
    make: (paramsAndState) => ({
      kind: 'payOpenInvoiceCancel',
      params: paramsAndState.params,
    }),
  },

  register: {
    make: (paramsAndState) => ({
      kind: 'register',
      params: paramsAndState.params,
    }),
  },
  registerStudent: {
    make: (paramsAndState) => ({
      kind: 'registerStudent',
      params: paramsAndState.params,
    }),
  },
  registerPromo: {
    make: (paramsAndState) => ({
      kind: 'registerPromo',
      params: paramsAndState.params,
    }),
  },
  activateAccount: {
    make: (paramsAndState) => ({
      kind: 'activateAccount',
      params: paramsAndState.params,
    }),
  },
  login: {
    make: (paramsAndState) => ({
      kind: 'login',
      params: paramsAndState.params,
    }),
  },
  logout: {
    make: (paramsAndState) => ({
      kind: 'logout',
      params: paramsAndState.params,
    }),
  },
  requestResetPassword: {
    make: (paramsAndState) => ({
      kind: 'requestResetPassword',
      params: paramsAndState.params,
    }),
  },
  resetPassword: {
    make: (paramsAndState) => ({
      kind: 'resetPassword',
      params: paramsAndState.params,
    }),
  },

  studentSubscription: {
    make: (paramsAndState) => ({
      kind: 'studentSubscription',
      params: paramsAndState.params,
      pageState: initialStudentSubscriptionState(
        paramsAndState.state.subscriptionState.userSubscriptions
      ),
    }),
  },
}

export const routeUpdaters = (
  language: string
): RouteUpdaters<RootState, Pages> => ({
  notFound: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.notFound.make({ params: params, state: s0 }),
      }),
    url: '',
  },
  homeRedirect: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.homeRedirect.make({ params: params, state: s0 }),
      }),
    url: '/',
  },
  home: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.home.make({ params: params, state: s0 }),
      }),
    url: language === 'en' ? '/en' : '/nl',
  },
  componentOverview: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.componentOverview.make({
          params: params,
          state: s0,
        }),
      }),
    url: language === 'en' ? '/en/components' : '/nl/componenten',
  },
  componentDetail: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.componentDetail.make({ params: params, state: s0 }),
      }),
    url:
      language == 'en'
        ? '/en/components/:componentIdAndName'
        : '/nl/componenten/:componentIdAndName',
  },
  componentEdit: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.componentEdit.make({ params: params, state: s0 }),
      }),
    url:
      language == 'en'
        ? '/en/components/:componentId/edit'
        : '/nl/componenten/:componentId/edit',
  },
  blogOverview: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.blogOverview.make({ params: params, state: s0 }),
      }),
    url: language == 'en' ? '/en/blog' : '/nl/blog',
  },
  blogDetail: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.blogDetail.make({ params: params, state: s0 }),
      }),
    url: language == 'en' ? '/en/blog/:blogSlug' : '/nl/blog/:blogSlug',
  },
  search: {
    jumpTo:
      (params: PageParams<Pages['search']>): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.search.make({ params: params, state: s0 }),
        header: { ...s0.header, searchString: params.term ? params.term : '' },
      }),
    url: language == 'en' ? '/en/search' : '/nl/search',
  },
  dashboard: {
    jumpTo:
      (params: PageParams<Pages['dashboard']>): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.dashboard.make({ params: params, state: s0 }),
      }),
    url: language == 'en' ? '/en/dashboard' : '/nl/dashboard',
  },
  collectionDetail: {
    jumpTo:
      (params: PageParams<Pages['collectionDetail']>): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.collectionDetail.make({
          params: params,
          state: s0,
        }),
      }),
    url:
      language == 'en'
        ? '/en/dashboard/collection/:collectionId'
        : '/nl/dashboard/collectie/:collectionId',
  },
  knowledgeOverview: {
    jumpTo:
      (params: PageParams<Pages['knowledgeOverview']>): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.knowledgeOverview.make({
          params: params,
          state: s0,
        }),
      }),
    url: language == 'en' ? '/en/knowledge' : '/nl/kennis',
  },
  knowledgeTypeOverview: {
    jumpTo:
      (
        params: PageParams<Pages['knowledgeTypeOverview']>
      ): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.knowledgeTypeOverview.make({
          params: params,
          state: s0,
        }),
      }),
    url:
      language == 'en'
        ? '/en/knowledge/:knowledgeTypeTitle'
        : '/nl/kennis/:knowledgeTypeTitle',
  },
  knowledgeDetail: {
    jumpTo:
      (params: PageParams<Pages['knowledgeDetail']>): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.knowledgeDetail.make({ params: params, state: s0 }),
      }),
    url:
      language == 'en'
        ? '/en/knowledge/:knowledgeTypeTitle/:knowledgeSlug'
        : '/nl/kennis/:knowledgeTypeTitle/:knowledgeSlug',
  },
  seasonalCalendar: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.seasonalCalendar.make({
          params: params,
          state: s0,
        }),
      }),
    url: language == 'en' ? '/en/seasonal-calendar' : '/nl/seizoenskalender',
  },
  page: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.page.make({ params: params, state: s0 }),
      }),
    url: language == 'en' ? '/en/:pageSlug' : '/nl/:pageSlug',
  },
  contact: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.contact.make({ params: params, state: s0 }),
      }),
    url: language == 'en' ? '/en/contact' : '/nl/contact',
  },
  dishesOverview: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.dishesOverview.make({ params: params, state: s0 }),
      }),
    url: language == 'en' ? '/en/dishes' : '/nl/gerechten',
  },
  dishesDetail: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.dishesDetail.make({ params: params, state: s0 }),
      }),
    url: language == 'en' ? '/en/dishes/:dishSlug' : '/nl/gerechten/:dishSlug',
  },

  accountDetails: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.accountDetails.make({ params: params, state: s0 }),
        subscriptionState: subscriptionUpdaters.overview(),
      }),
    url: language == 'en' ? '/en/account-details' : '/nl/accountgegevens',
  },
  updatePayment: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.updatePayment.make({ params: params, state: s0 }),
      }),
    url: language == 'en' ? '/en/update-payment-method' : '/nl/betaalmethode-bijwerken',
  },
  updatePaymentRedirect: {
    jumpTo:
    (params): Updater<RootState> =>
    (s0) => ({
      ...s0,
      page: routeBuilders.updatePaymentRedirect.make({ params: params, state: s0 }),
    }),
  url: language == 'en' ? '/en/update-payment-method-redirect' : '/nl/betaalmethode-bijwerken-redirect',
  },
  thankYouUpdatePayment: {
      jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.thankYouUpdatePayment.make({ params: params, state: s0 }),
      }),
    url: language == 'en' ? '/en/update-payment-method-success' : '/nl/betaalmethode-bijwerken-succes',
  },
  upgrade: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.upgrade.make({ params: params, state: s0 }),
        subscriptionState: subscriptionUpdaters.startNewSubscriptionFlow()(
          s0.subscriptionState
        ),
      }),
    url: language == 'en' ? '/en/upgrade-account' : '/nl/upgrade-account',
  },

  subscriptionSuccessRedirect: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.subscriptionSuccessRedirect.make({
          params: params,
          state: s0,
        }),
      }),
    url: '/subscription-success',
  },
  subscriptionSuccess: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.subscriptionSuccess.make({
          params: params,
          state: s0,
        }),
        subscriptionState: subscriptionUpdaters.subscriptionRedirect(
          'subscription-success'
        )(s0.subscriptionState),
      }),
    url: language == 'en' ? '/en/upgrade-succeeded' : '/nl/upgrade-geslaagd',
  },
  subscriptionFailureRedirect: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.subscriptionFailureRedirect.make({
          params: params,
          state: s0,
        }),
      }),
    url: '/subscription-failure',
  },
  subscriptionFailure: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.subscriptionFailure.make({
          params: params,
          state: s0,
        }),
        subscriptionState: subscriptionUpdaters.subscriptionRedirect(
          'subscription-failure'
        )(s0.subscriptionState),
      }),
    url: language == 'en' ? '/en/upgrade-failed' : '/nl/upgrade-mislukt',
  },
  subscriptionCancelRedirect: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.subscriptionCancelRedirect.make({
          params: params,
          state: s0,
        }),
      }),
    url: '/subscription-cancel',
  },
  subscriptionCancel: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.subscriptionCancel.make({
          params: params,
          state: s0,
        }),
        subscriptionState: subscriptionUpdaters.subscriptionRedirect(
          'subscription-cancel'
        )(s0.subscriptionState),
      }),
    url: language == 'en' ? '/en/upgrade-cancelled' : '/nl/upgrade-geannuleerd',
  },
  subscriptionStopPayment: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.subscriptionStopPayment.make({
          params: params,
          state: s0,
        }),
      }),
    url: language == 'en' ? '/en/account-details/stop-payment' : '/nl/accountgegevens/stop-betaling',
  },
  changePaymentMethodSuccessRedirect: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.changePaymentMethodSuccessRedirect.make({
          params: params,
          state: s0,
        }),
      }),
    url: '/change-payment-method-success',
  },
  changePaymentMethodSuccess: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.changePaymentMethodSuccess.make({
          params: params,
          state: s0,
        }),
        subscriptionState: subscriptionUpdaters.subscriptionRedirect(
          'change-payment-method-success'
        )(s0.subscriptionState),
      }),
    url:
      language == 'en'
        ? '/en/payment-details-changed'
        : '/nl/betaalgegevens-gewijzigd',
  },
  changePaymentMethodFailureRedirect: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.changePaymentMethodFailureRedirect.make({
          params: params,
          state: s0,
        }),
      }),
    url: '/change-payment-method-failure',
  },
  changePaymentMethodFailure: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.changePaymentMethodFailure.make({
          params: params,
          state: s0,
        }),
        subscriptionState: subscriptionUpdaters.subscriptionRedirect(
          'change-payment-method-failure'
        )(s0.subscriptionState),
      }),
    url:
      language == 'en'
        ? '/en/payment-details-change-failed'
        : '/nl/betaalgegevens-wijziging-mislukt',
  },
  changePaymentMethodCancelRedirect: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.changePaymentMethodCancelRedirect.make({
          params: params,
          state: s0,
        }),
      }),
    url: '/change-payment-method-cancel',
  },
  changePaymentMethodCancel: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.changePaymentMethodCancel.make({
          params: params,
          state: s0,
        }),
        subscriptionState: subscriptionUpdaters.subscriptionRedirect(
          'change-payment-method-cancel'
        )(s0.subscriptionState),
      }),
    url:
      language == 'en'
        ? '/en/payment-details-change-cancelled'
        : '/nl/betaalgegevens-wijziging-geannuleerd',
  },
  payOpenInvoiceSuccessRedirect: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.payOpenInvoiceSuccessRedirect.make({
          params: params,
          state: s0,
        }),
      }),
    url: '/pay-open-invoice-success',
  },
  payOpenInvoiceSuccess: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.payOpenInvoiceSuccess.make({
          params: params,
          state: s0,
        }),
        subscriptionState: subscriptionUpdaters.subscriptionRedirect(
          'pay-open-invoice-success'
        )(s0.subscriptionState),
      }),
    url: language == 'en' ? '/en/invoice-paid' : '/nl/factuur-betaald',
  },
  payOpenInvoiceFailureRedirect: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.payOpenInvoiceFailureRedirect.make({
          params: params,
          state: s0,
        }),
      }),
    url: '/pay-open-invoice-failure',
  },
  payOpenInvoiceFailure: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.payOpenInvoiceFailure.make({
          params: params,
          state: s0,
        }),
        subscriptionState: subscriptionUpdaters.subscriptionRedirect(
          'pay-open-invoice-failure'
        )(s0.subscriptionState),
      }),
    url:
      language == 'en'
        ? '/en/invoice-payment-failed'
        : '/nl/factuur-betaling-mislukt',
  },
  payOpenInvoiceCancelRedirect: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.payOpenInvoiceCancelRedirect.make({
          params: params,
          state: s0,
        }),
      }),
    url: '/pay-open-invoice-cancel',
  },
  payOpenInvoiceCancel: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.payOpenInvoiceCancel.make({
          params: params,
          state: s0,
        }),
        subscriptionState: subscriptionUpdaters.subscriptionRedirect(
          'pay-open-invoice-cancel'
        )(s0.subscriptionState),
      }),
    url:
      language == 'en'
        ? '/en/invoice-payment-cancelled'
        : '/nl/factuur-betaling-geannuleerd',
  },

  register: {
    jumpTo:
      (params: PageParams<Pages['register']>): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.register.make({ params: params, state: s0 }),
      }),
    url:
      language == 'en' ? '/en/start-your-trial' : '/nl/start-jouw-proefperiode',
  },
  registerStudent: {
    jumpTo:
      (params: PageParams<Pages['registerStudent']>): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.registerStudent.make({ params: params, state: s0 }),
      }),
    url: '/nl/start-jouw-student-account',
  },
  registerPromo: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.registerPromo.make({ params: params, state: s0 }),
      }),
    url: language == 'en' ? '/en/promo' : '/nl/promo',
  },
  activateAccount: {
    jumpTo:
      (params: PageParams<Pages['activateAccount']>): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.activateAccount.make({ params: params, state: s0 }),
      }),
    url: language == 'en' ? '/en/activate-account' : '/nl/account-activeren',
  },
  requestResetPassword: {
    jumpTo:
      (params: PageParams<Pages['requestResetPassword']>): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.requestResetPassword.make({
          params: params,
          state: s0,
        }),
      }),
    url: language == 'en' ? '/en/forgot-password' : '/nl/wachtwoord-vergeten',
  },
  resetPassword: {
    jumpTo:
      (params: PageParams<Pages['resetPassword']>): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.resetPassword.make({ params: params, state: s0 }),
      }),
    url:
      language == 'en'
        ? '/en/create-new-password'
        : '/nl/nieuw-wachtwoord-aanmaken',
  },
  login: {
    jumpTo:
      (params: PageParams<Pages['login']>): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.login.make({ params: params, state: s0 }),
      }),
    url: language == 'en' ? '/en/login' : '/nl/login',
  },
  logout: {
    jumpTo:
      (params: PageParams<Pages['logout']>): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.logout.make({ params: params, state: s0 }),
      }),
    url: language == 'en' ? '/en/logout' : '/nl/uitloggen',
  },

  studentSubscription: {
    jumpTo:
      (params): Updater<RootState> =>
      (s0) => ({
        ...s0,
        page: routeBuilders.studentSubscription.make({
          params: params,
          state: s0,
        }),
      }),
    url: language == 'en' ? '/en/student' : '/nl/student',
  },
})
